import * as React from "react";
import { useEffect, useRef, useState } from "react";
import Icon from "../../Icon";
import styles from "./styles.module.scss";
import Boop from "src/components/Boop";
import Dialog from "src/components/Dialog";
import { motion, AnimatePresence } from "framer-motion";
import { useFloater, DeckType } from "@src/hooks/useFloater";
import LoginAsGuestModal from "src/components/modals/LoginAsGuestModal";
import { FloaterProps } from "@src/components/Floater";

const FloaterItem: React.FC<{
  item: DeckType;
  onOpen?: (title: string) => void;
}> = ({ item, onOpen }) => (
  <Dialog
    key={item.title}
    fillScreen={item.fillScreen}
    trigger={
      <Boop y={2}>
        <div className={styles["floater-icon"]}>
          <div className={styles["floater-icon-box"]}>
            <Icon iconName={item.icon} />
          </div>
          <p className={`${styles["floater-paragraph"]} font-jost`}>
            {item.title}
          </p>
        </div>
      </Boop>
    }
    open={item.open}
    onOpenChange={(open) => {
      if (open && onOpen) {
        onOpen(item.title);
      }
      item.setOpen(open);
    }}
  >
    {item.modal}
  </Dialog>
);

const Floater: React.FC<FloaterProps> = ({
  getEventStreamersData,
  eventId,
  slugData,
  liveSchedules,
  hasWallet,
  access_control,
  qr_code,
  accessToken,
  eventUuid,
  getAsoebiData,
  admits,
  invitation,
  isInvitedToEvent,
}) => {
  const {
    visibleItems,
    overflowItems,
    hasOverflow,
    hasItemsToShow,
    showGuestLogin,
    isExpanded,
    toggleExpanded,
    trackView,
    openGuestLogin,
    setOpenGuestLogin,
  } = useFloater({
    getEventStreamersData,
    eventId,
    slugData,
    liveSchedules,
    access_control,
    qr_code: qr_code ?? "",
    accessToken: accessToken ?? null,
    getAsoebiData,
    eventUuid,
    admits: admits ?? 1,
    invitation,
    isInvitedToEvent: isInvitedToEvent ?? false,
  });

  const [scrollHidden, setScrollHidden] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const floaterRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onScroll = () => {
      // Only apply on mobile (touch devices / narrow screens)
      if (window.innerWidth >= 768) return;
      setScrollHidden(true);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => setScrollHidden(false), 2000);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isPanelOpen) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (!floaterRef.current) return;
      const target = event.target as HTMLElement | null;

      if (target?.closest('[data-floater-ignore-outside-click="true"]')) {
        return;
      }

      if (!floaterRef.current.contains(event.target as Node)) {
        setIsPanelOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isPanelOpen]);

  return hasItemsToShow || showGuestLogin ? (
    <AnimatePresence>
      {!scrollHidden && (
        <motion.div
          key="floater-wrapper"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={styles["floater"]}
          ref={floaterRef}
        >
          <button
            type="button"
            onClick={() => setIsPanelOpen((prev) => !prev)}
            className={styles["floater-toggle"]}
            aria-label={
              isPanelOpen ? "Close quick actions" : "Open quick actions"
            }
            aria-expanded={isPanelOpen}
          >
            <span
              className={`${styles["floater-toggle-icon"]} ${
                isPanelOpen ? styles["floater-toggle-icon--open"] : ""
              }`}
              aria-hidden="true"
            />
          </button>

          <AnimatePresence>
            {isPanelOpen && (
              <motion.div
                key="floater-panel"
                initial={{ opacity: 0, scale: 0.85, y: 8 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  transition: {
                    duration: 0.3,
                    ease: [0.34, 1.56, 0.64, 1],
                  },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.88,
                  y: 6,
                  transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
                }}
                style={{ transformOrigin: "bottom left" }}
                layout
                className={styles["floater-container"]}
              >
                <div className={styles["floater-row"]}>
                  {showGuestLogin && (
                    <>
                      <button
                        type="button"
                        className={styles["floater-guest-btn"]}
                        aria-label="Login as guest"
                        data-guest-login-trigger
                        onClick={() => setOpenGuestLogin(true)}
                      >
                        <span
                          className={`${styles["floater-guest-btn__text"]} font-jost uppercase`}
                        >
                          Login as guest
                        </span>
                      </button>
                      <div className={styles["floater-divider"]} aria-hidden />
                      <Dialog
                        fillScreen={false}
                        open={openGuestLogin}
                        onOpenChange={setOpenGuestLogin}
                      >
                        <LoginAsGuestModal
                          eventUuid={eventUuid}
                          setOpen={setOpenGuestLogin}
                        />
                      </Dialog>
                    </>
                  )}

                  <div className={styles["floater-items"]}>
                    {visibleItems.map((item) => (
                      <FloaterItem
                        key={item.title}
                        item={item}
                        onOpen={trackView}
                      />
                    ))}

                    {hasOverflow && overflowItems.length > 0 && (
                      <Boop y={2}>
                        <button
                          className={styles["floater-more-btn"]}
                          onClick={toggleExpanded}
                          aria-label={
                            isExpanded
                              ? "Show less options"
                              : "Show more options"
                          }
                        >
                          <div className={styles["floater-icon"]}>
                            <div className={styles["floater-icon-box"]}>
                              <Icon
                                iconName={
                                  isExpanded ? "chevronUp" : "chevronDown"
                                }
                              />
                            </div>
                            <p
                              className={`${styles["floater-paragraph"]} font-jost uppercase`}
                            >
                              {isExpanded ? "Less" : "More"}
                            </p>
                          </div>
                        </button>
                      </Boop>
                    )}
                  </div>
                </div>

                {hasOverflow && overflowItems.length > 0 && (
                  <div
                    className={styles["floater-overflow-wrapper"]}
                    style={{
                      gridTemplateRows: isExpanded ? "1fr" : "0fr",
                    }}
                  >
                    <div
                      className={`${styles["floater-overflow"]} ${
                        isExpanded ? styles["floater-overflow--expanded"] : ""
                      }`}
                    >
                      {overflowItems.map((item) => (
                        <FloaterItem
                          key={item.title}
                          item={item}
                          onOpen={trackView}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  ) : null;
};

export default Floater;
